import type { APITeoMarker, MarkerGroupName } from '@/teoCore/TeoApi';
import type { TeoText } from '@/teoCore/text/TeoText';

import { markerColors } from './config';
import { EditorMarkings } from './EditorMarkings.svelte';
import type EditorContent from './lexical/EditorContent';

export type EditorMarkingStyle = 'background' | 'underline' | 'block';

export interface TeoMarker {
  start: number;
  end: number;
  type: string;
  color: string;
  hoverColor?: string;
  underlineColor?: string;
  style: EditorMarkingStyle;
  id: string;
  isHighlighted?: boolean;
  rawMarker: APITeoMarker;
  markerGroup: MarkerGroupName;
  icon?: boolean;
}

export class EditorMarkerManager {
  markerList: TeoMarker[] = [];
  private sizeChangeTimeout: number = 0;
  private _editorContent: EditorContent | null = null;
  private listenerRemovers: (() => void)[] = [];
  markings: EditorMarkings;
  private currentlyShownMarkers: string[] = [];
  private markerResetQueue: Promise<void> = Promise.resolve();
  private pendingResets = 0;

  constructor(
    private text: TeoText,
    container: HTMLElement,
  ) {
    this.markings = new EditorMarkings(this, container);
    this.listenerRemovers.push(
      text.events.addEventListener('gotAnalysis', (result) => {
        this.resetMarkers(result.getMarkers(), 'analysis');
      }),
      text.wordLists.brandFit.events.addEventListener('scanPending', (pending) => {
        if (pending) this.resetMarkers([], 'brand-scanner');
      }),
      text.wordLists.dictionary.events.addEventListener('scanPending', (pending) => {
        if (pending) this.resetMarkers([], 'dictionary');
      }),
      text.wordLists.brandFit.events.addEventListener('gotScanResult', () => {
        this.resetMarkers(text.wordLists.brandFit.markers, 'brand-scanner');
      }),
      text.wordLists.dictionary.events.addEventListener('gotScanResult', () => {
        this.resetMarkers(text.wordLists.dictionary.markers, 'dictionary');
      }),
      text.spellCheck.events.addEventListener('spellCheckReceived', (detail) => {
        this.resetMarkers(detail.markers, 'spellcheck');
      }),
      text.diff.events.addEventListener('markersUpdated', (markers) => {
        this.resetMarkers(markers, 'diff');
      }),
      text.ui.events.addEventListener('markers', (markers) => {
        this.showMarkers(markers);
      }),
      text.markers.events.addEventListener('markerReplacement', (event) => {
        this.replaceMarkerContent(event.markerId, event.newContent);
        this.markings.addEditedMarker(event.markerId);
      }),
      text.markers.events.addEventListener('markersFocused', (event) => {
        this.markings.setFocusedMarkers(event.markerIds);
      }),
      text.markers.events.addEventListener('markerClickTriggered', (event) => {
        this.clickMarker(event.markerId);
      }),
    );
  }

  get editorContent(): EditorContent | null {
    return this._editorContent;
  }

  set editorContent(editorContent: EditorContent) {
    this._editorContent = editorContent;
    this.listenerRemovers.push(
      this._editorContent.events.addEventListener('contentChanged', () => {
        this.markings.reRender();
      }),
    );
    if (this.text.analysis) {
      this.resetMarkers(this.text.analysis.getMarkers(), 'analysis');
    }
    if (this.text.wordLists.brandFit.markers) {
      this.resetMarkers(this.text.wordLists.brandFit.markers, 'brand-scanner');
    }
    if (this.text.wordLists.dictionary.markers) {
      this.resetMarkers(this.text.wordLists.dictionary.markers, 'dictionary');
    }
    if (this.text.wordLists.whiteList.markers) {
      this.resetMarkers(this.text.wordLists.whiteList.markers, 'white-list');
    }
    if (this.text.spellCheck.markers) {
      this.resetMarkers(this.text.spellCheck.markers, 'spellcheck');
    }
  }

  getMarkerById(markerId: string): TeoMarker | null {
    return this.markerList.find((marker) => marker.id === markerId) || null;
  }

  addMarkers(markers: TeoMarker[], onUpdate?: () => void): void {
    this.markerList = this.markerList.concat(markers);
    this._editorContent?.addMarkers(markers, () => {
      if (this.text.ui.state.markers.length > 0) {
        // this._editorContent?.hideMarkers();
        this.markings.hideMarkers();
        this.showMarkers(this.text.ui.state.markers);
      }
      if (onUpdate) {
        onUpdate();
      }
    });
  }

  showMarkers(markerNames: string[]): void {
    const markers = this.markerList.filter((marker) => markerNames.includes(marker.type));
    this.markings.showMarkers(markers, this.text.markers.clickableMarkers);
    this.currentlyShownMarkers = markerNames;
  }

  hideMarkers(): void {
    this.markings.hideMarkers();
    this.currentlyShownMarkers = [];
  }

  clearMarkers(groupName?: MarkerGroupName): void {
    if (groupName) {
      const markerIds: string[] = [];
      this.markerList = this.markerList.filter((marker) => {
        if (marker.markerGroup === groupName) {
          markerIds.push(marker.id);
          return false;
        }
        return true;
      });
      if (markerIds.length !== 0) {
        this._editorContent?.clearMarkers(markerIds);
      }
    } else {
      this.markerList = [];
      this._editorContent?.clearMarkers();
    }
  }

  sizeChangeUpdate() {
    if (this.sizeChangeTimeout !== 0) {
      window.clearTimeout(this.sizeChangeTimeout);
    }
    this.sizeChangeTimeout = window.setTimeout(() => {
      this.markings.reRender();
    }, 50);
  }

  private resetMarkers(
    markers: APITeoMarker[],
    markerGroup: MarkerGroupName,
    onUpdate?: () => void,
  ): void {
    this.pendingResets++;
    this.markerResetQueue = this.markerResetQueue
      .then(
        () =>
          new Promise<void>((resolve) => {
            // console.log('Resetting markers for group', markerGroup, markers);
            this.markings.clearEditedMarkers();
            this.hideMarkers();
            this.clearMarkers(markerGroup);
            this.addMarkers(this.createTeoMarkers(markers, markerGroup), () => {
              if (onUpdate) onUpdate();
              resolve(); // Only resolve when addMarkers' onUpdate is called
            });
          }),
      )
      .catch((err) => {
        console.error('Error in resetMarkers queue:', err);
      });
  }

  clickMarker(markerId: string) {
    const marker = this.markerList.find((marker) => marker.id === markerId);
    if (!marker) {
      console.error('Marker not found', markerId);
      return;
    }
    this.text.markers.clickMarker(markerId, marker.rawMarker);
  }

  replaceMarkerContent(markerId: string, content: string) {
    this.editorContent?.replaceMarkerContent(markerId, content);
  }

  private createTeoMarkers(apiMarkers: APITeoMarker[], markerGroup: MarkerGroupName): TeoMarker[] {
    return this.adjustMarkerOffsets(apiMarkers)
      .map((marker) => {
        if (marker.length === 0) {
          return null;
        }
        if (marker.relation !== null) {
          marker.relation = marker.relation.toString();
        }
        return this.createTeoMarker(marker, markerGroup);
      })
      .filter((marker): marker is TeoMarker => marker !== null);
  }

  private createTeoMarker(apiMarker: APITeoMarker, markerGroup: MarkerGroupName): TeoMarker {
    if (apiMarker.marker && apiMarker.marker.startsWith('paragraph')) {
      return this.createParagraphTeoMarker(apiMarker, markerGroup);
    } else if (apiMarker.marker && apiMarker.marker == 'sentence') {
      return this.createSentenceTeoMarker(apiMarker, markerGroup);
    } else if (
      apiMarker.groupName === 'brand-scanner' ||
      apiMarker.groupName === 'dictionary' ||
      apiMarker.groupName === 'spellcheck' ||
      apiMarker.groupName === 'white-list'
    ) {
      return this.createReplaceableTeoMarker(apiMarker, markerGroup);
    } else if (apiMarker.marker === 'editor_diff') {
      return this.createDiffTeoMarker(apiMarker, markerGroup);
    }
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: markerColors[apiMarker.marker] || 'rgba(255,100,100,0.5)',
      id: apiMarker.relation ?? crypto.randomUUID(),
      style: 'background',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  private createDiffTeoMarker(apiMarker: APITeoMarker, markerGroup: MarkerGroupName): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: markerColors[apiMarker.marker] || 'rgba(255,100,100,0.5)',
      id: apiMarker.relation ?? crypto.randomUUID(),
      style: 'underline',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  private createParagraphTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: 'paragraph',
      color: markerColors['paragraph'] || 'rgba(255,100,100,0.5)',
      hoverColor: markerColors['paragraph-hover'],
      id: apiMarker.relation ?? crypto.randomUUID(),
      style: 'block',
      markerGroup,
      rawMarker: apiMarker,
      icon: true,
    };
  }

  private createSentenceTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: 'sentence',
      color: markerColors['paragraph'] || 'rgba(255,100,100,0.5)',
      hoverColor: markerColors['paragraph-hover'],
      id: apiMarker.relation ?? crypto.randomUUID(),
      style: 'background',
      markerGroup,
      rawMarker: apiMarker,
      icon: true,
    };
  }

  private createReplaceableTeoMarker(
    apiMarker: APITeoMarker,
    markerGroup: MarkerGroupName,
  ): TeoMarker {
    return {
      start: apiMarker.offset,
      end: apiMarker.offset + apiMarker.length,
      type: apiMarker.marker,
      color: (apiMarker.color ? markerColors[apiMarker.color] : false) || 'rgba(100,255,100,0.5)',
      underlineColor: apiMarker.color ? markerColors['s' + apiMarker.color] : undefined,
      id: apiMarker.relation ?? crypto.randomUUID(),
      style: 'underline',
      markerGroup,
      rawMarker: apiMarker,
    };
  }

  /**
   * The API counts (multibyte) characters, not bytes, while JS counts UTF-16 code units.
   * This function adjusts the offsets to account for the difference. The markers are sorted by offset.
   * @param markers - The markers to adjust.
   * @returns The adjusted markers, sorted by offset.
   */
  private adjustMarkerOffsets(markers: APITeoMarker[]): APITeoMarker[] {
    const text = this.text.text;
    if (text.length === [...text].length) {
      return markers;
    }
    console.log('adjustMarkerOffsets', text.length, [...text].length);
    markers.sort((a, b) => a.offset - b.offset);
    const newOffsets: number[] = [];
    let currentMarkerIndex = 0;
    const openMarkers: number[] = [];
    let utf16Offset = 0;
    for (const [charOffset, character] of Array.from(text).entries()) {
      for (let idx = openMarkers.length - 1; idx >= 0; idx--) {
        const openMarker = openMarkers[idx];
        if (
          openMarker === undefined ||
          newOffsets[openMarker] === undefined ||
          markers[openMarker] === undefined
        ) {
          console.warn('Invalid open marker');
          continue;
        }
        if (markers[openMarker].offset + markers[openMarker].length <= charOffset) {
          markers[openMarker].offset = newOffsets[openMarker];
          markers[openMarker].length = utf16Offset - newOffsets[openMarker];
          openMarkers.splice(idx, 1);
        }
      }
      for (let i = currentMarkerIndex; i < markers.length; i++) {
        const marker = markers[i];
        if (marker !== undefined && marker.offset === charOffset) {
          openMarkers.push(i);
          newOffsets[i] = utf16Offset;
        } else {
          currentMarkerIndex = i;
          break;
        }
      }
      utf16Offset += character.length;
    }
    for (const openMarker of openMarkers) {
      if (newOffsets[openMarker] === undefined || markers[openMarker] === undefined) {
        console.warn('Invalid open marker');
        continue;
      }
      markers[openMarker].offset = newOffsets[openMarker];
      markers[openMarker].length = utf16Offset - newOffsets[openMarker];
    }
    return markers;
  }

  destroy() {
    this.listenerRemovers.forEach((remove) => remove());
  }
}
